import { Injectable, inject } from '@angular/core';
import { Feature, Geometry, LineString } from '@turf/turf';

import { MapStateService } from '../map-state.service';
import {
    RenderDrawRouteLayer, RenderLinesLayer, RenderRouteTilesetLayer, RenderStopsLayer, RenderStopsTilesetLayer, RenderVehiclesLayer
} from './map-layer-definitions';
import { MapboxHelper } from './mapbox-map-helper';
import { MapLayerFactory } from './mapbox-map-layer-factory';
import { Layers } from './models';

import { Subject, debounceTime, filter, take } from 'rxjs';
import { MapDataSetLayer, MapStyle, Theme } from '../../shared/models';

import * as mapboxgl from 'mapbox-gl';

// github issue: https://github.com/mapbox/mapbox-gl-draw/issues/725#issuecomment-352021407
import MapboxDraw from '@mapbox/mapbox-gl-draw';

import { AppStateService } from '../../core/services/app-state.service';
import { VEHICLE_COLOR_RGB } from '../../shared/models/vehicle';
import { Vehicle } from '@modules/shared/models';
import { drawStyle } from '../models/draw-style';
import { MapState, MAP_STATE } from '../models/map-state';
import { DrawRoutingService, DRAW_MODE } from '../services/draw-routing.service';
import { RoutingService, ROUTING_PROFILE } from '../services/routing.service';
import { VehicleIcon } from './icons/vehicle-icon';
import { createVehicleTextBadge } from './icons/vehicle-text-badge';
import { WarningIcon } from './icons/warning-icon';

import * as turf from '@turf/turf';
import * as _ from 'lodash';
import { ConfigService } from 'src/config';
import { RenderTimingPointLayer } from './map-layer-definitions/timing-point-layer';
import { LoggingService } from '@modules/shared/services/logging.service';
import { VehicleStore } from '@modules/core/store';
import { HtmlPopupHelper } from './html-popup-helper';

// eslint-disable-next-line @typescript-eslint/naming-convention, no-shadow
export enum MAP_CREATE_GEO_OPTIONS {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    POINT = 'POINT',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    LINESTRING = 'LINESTRING',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    POLYGON = 'POLYGON'
}

@Injectable({
    providedIn: 'root'
})
export class MapboxService {
    private readonly _mapboxHelper = inject(MapboxHelper);
    private readonly _mapLayerFactory = inject(MapLayerFactory);
    private readonly _mapStateService = inject(MapStateService);
    private readonly _routingService = inject(RoutingService);
    private readonly _drawRouting = inject(DrawRoutingService);
    private readonly _appStateService = inject(AppStateService);
    private readonly _configService = inject(ConfigService);
    private readonly _logger = inject(LoggingService);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _htmlPopupHelper = inject(HtmlPopupHelper);

    private _map: mapboxgl.Map;
    private _draw: MapboxDraw;
    private _attributionControl: mapboxgl.AttributionControl;
    private _theme: Theme = Theme.Light;
    private _defaultFilters: any[] = [];
    private _popupReference: mapboxgl.Popup;
    private _layersToDraw: MapDataSetLayer[] = [];
    private _style: MapStyle;
    private _lastFlyTo: [number, number];

    private readonly _layerVisibilityNames = []; // a list of all layers that are set to visible
    private readonly _selectedPopupLayers: string[] = [Layers.Vehicle];
    private readonly _triggerRedrawVehicleLayer$: Subject<Feature[] | void> = new Subject();
    private readonly _redrawVehicleLayer$ = this._triggerRedrawVehicleLayer$.asObservable();

    // Baseline symmetric breathing room around fitBounds. We do not keep
    // persistent left/right blade-width state on the service; fitBounds derives
    // any additional side inset from current layout at call time.
    private readonly _defaultPadding: mapboxgl.PaddingOptions = { top: 40, bottom: 40, left: 40, right: 40 };
    private _padding: mapboxgl.PaddingOptions = this._defaultPadding;

    private readonly _mapDrawFeatureChange$: Subject<Feature> = new Subject();
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public mapDrawFeatureChange$ = this._mapDrawFeatureChange$.asObservable();

    // map state values
    private set _mapState(value: MAP_STATE) {
        this._mapStateService.setMapState(value);
    }

    private get _mapState() {
        return this._mapStateService.state()?.state;
    }

    private _routingProfile: ROUTING_PROFILE = ROUTING_PROFILE.DRIVING;
    private _drawIsLoading = false;
    private _prevCursor = '';
    private _selectedFeatureCollections: { [key: string]: any } = {};
    private _renderedLayerData: { [key: string]: any } = {}; // in order to have several components to draw same layers
    
    // Popup tracking to prevent excessive calls
    private _currentPopupInventoryNumber: string | undefined;
    private _popupDebounceTimer: number | undefined;

    private readonly _vehicleHoverSubject$ = new Subject<{ vehicle: Vehicle, clientX: number, clientY: number } | null>();
    public readonly vehicleHover$ = this._vehicleHoverSubject$.asObservable();

    private readonly _routeTilesetUrl;
    private readonly _routeTilesetName;
    private readonly _stopsTilesetUrl;
    private readonly _stopsTilesetName;


    constructor() {
        this._routeTilesetUrl = this._configService.mapRouteTilesetUrl;
        this._routeTilesetName = this._configService.mapRouteTilesetName;

        this._stopsTilesetUrl = this._configService.mapStopsTilesetUrl;
        this._stopsTilesetName = this._configService.mapStopsTilesetName;
    }

    public initMap(mapGL: mapboxgl.Map, style: MapStyle) {
        this._map = mapGL;

        // The 'loaded' flag is shared via providedIn: 'root' MapStateService. If the map
        // component is destroyed and re-created we must reset it so guards (fitBounds,
        // renderData, ...) don't run against a fresh instance whose 'styledata' event
        // has not fired yet. The flag is flipped back to true in _setupMapLoadedListener.
        this._mapStateService.loaded = false;

        const mapState: MapState = this._mapStateService.state();
        // Always start with the symmetric _defaultPadding so the camera center
        // stays anchored to the canvas center. We intentionally ignore any
        // persisted padding from a previous session here.
        this._map.setPadding(this._padding);

        if (mapState.center) {
            this._map.setCenter(mapState.center);
        }

        if (mapState.zoom) {
            this._map.setZoom(mapState.zoom);
        }

        // create renderers to the map
        this._createLayerRenderers();

        // add controls
        this._attributionControl = new mapboxgl.AttributionControl({ compact: true });
        this._map.addControl(this._attributionControl, 'bottom-left');

        this._draw = new MapboxDraw({
            displayControlsDefault: false,
            styles: drawStyle
        });
        this._map.addControl(this._draw);

        this._mapState = MAP_STATE.VIEW;

        this._setupMapEventListeners();
        this._map.resize();
        this._popupReference = this._mapboxHelper.createPopup(this._map);

        this._style = style;

        this._redrawVehicleLayer$.pipe(debounceTime(50)).subscribe((features: Feature[]) => {
            if (this._mapStateService.loaded) {
                this._prepareVehicleFeatures(features || []);
                const fc = this._createFeatureCollection(features || []);
                this._mapLayerFactory.renderLayer(Layers.Vehicle, this._map, fc);
            }
        });

        // All store subscribers is placed in map.service.ts
    }

    public clearCache() {
        mapboxgl.clearStorage();
    }

    public resize() {
        if (this._map) {
            this._map.resize();
        }
    }

    public triggerRedrawVehicleLayer(features: Feature[]) {
        this._triggerRedrawVehicleLayer$.next(features);
    }

    //#region  Public draw operations on the map
    public canRemoveEditFeature() {
        const fc = this._draw.getAll();
        return fc?.features?.length > 0;
    }

    public removeEditFeature() {
        this._draw.deleteAll();
        this._mapDrawFeatureChange$.next(undefined);
        this._mapState = MAP_STATE.CREATE_FEATURE;
    }

    public setCreateFeatureDrawMode(type: MAP_CREATE_GEO_OPTIONS) {
        let drawMode = '';
        switch (type) {
            case MAP_CREATE_GEO_OPTIONS.LINESTRING:
                drawMode = 'draw_line_string';
                break;
            case MAP_CREATE_GEO_OPTIONS.POINT:
                drawMode = 'draw_point';
                break;
            case MAP_CREATE_GEO_OPTIONS.POLYGON:
                drawMode = 'draw_polygon';
                break;
            default:
                break;
        }

        this._draw.changeMode(drawMode);
    }

    public setMapEditState(state: MAP_STATE) {
        this._mapState = state;
    }

    public clearEdit() {
        this._mapState = MAP_STATE.VIEW;
        this._draw.deleteAll();
        this._drawRouting.clear();
        this._routingProfile = ROUTING_PROFILE.DRIVING;
    }

    public set routingProfile(routingProfile: ROUTING_PROFILE) {
        if (!this._editRouteDrawState) {
            return;
        }

        this._routingProfile = routingProfile;
    }

    public get routingProfile() {
        return this._routingProfile;
    }

    //#endregion

    public fitBounds(bounds: mapboxgl.LngLatBoundsLike, relativePadding: number = 30): boolean {
        // Use our own "style loaded" flag (set on the 'styledata' event) instead of
        // mapboxgl.Map.loaded(), which also returns false while any tile source is still
        // fetching - with the continuously streaming RouteTileset/StopsTileset that is
        // almost always the case, so map.loaded() would make this method a no-op.
        if (!this._map || !this._mapStateService.loaded) return false;

        const canvas = this._map.getCanvas();
        // Check canvas.width (the actual pixel buffer, set by mapbox on resize) not clientWidth (CSS size).
        // clientWidth can be non-zero while the map transform is still uninitialized.
        if (!canvas || canvas.width === 0 || canvas.height === 0) return false;
        if (bounds == null) return false;
        try {
            // Keep horizontal padding symmetric so the geographic center lands
            // on the canvas center. Derive side inset from current left/right
            // layout occlusion (selection overview + left blades, right blades)
            // instead of a hardcoded value.
            const sideInset = Math.max(
                this._padding.left,
                this._padding.right,
                this._getLeftOcclusionWidth(),
                this._getRightBladeWrapperWidth()
            ) + relativePadding;
            const effectivePadding = {
                top: this._padding.top + relativePadding,
                bottom: this._padding.bottom + relativePadding,
                left: sideInset,
                right: sideInset
            };
            this._map.fitBounds(bounds, {
                padding: effectivePadding,
                easing: (t: number) => t * (2 - t),
                maxZoom: 14
            });
            return true;
        } catch {
            // fitBounds can throw on invalid bounds or if the map is not ready, but we check for that beforehand, so just catch and return false if it happens
            return false;
        }
    }

    public flyTo(location: [number, number], zoom?: number): void {
        if (!this._map) return;

        // prevent circular flyTo:s
        if (this._lastFlyTo) {
            if (this._lastFlyTo[0] !== location[0] && this._lastFlyTo[1] !== location[1]) {
                this._lastFlyTo = location;
            }
        } else {
            this._lastFlyTo = location;
        }

        const destination: mapboxgl.LngLat = new mapboxgl.LngLat(location[0], location[1]);
        const bounds = this._map.getBounds();

        // only move if destination is outside boundingbox
        if (!bounds.contains(destination)) {
            const options = {
                center: [location[0], location[1]] as [number, number],
                padding: this._padding,
                speed: 3,
                curve: 1
            };
            if (zoom) options['zoom'] = zoom;
            this._map.flyTo(options);
        } else {
            this._mapboxHelper.centerPosition(this._map, location, zoom, this._padding);
        }
    }

    public setZoom(zoom: number) {
        this._map.setZoom(zoom);
    }

    public setPadding(padding: mapboxgl.PaddingOptions) {
        this._padding = padding;
    }

    public resetPadding() {
        this._padding = this._defaultPadding;
    }

    public setCenter(location: number[]) {
        if (this._map) {
            this._map.setCenter([location[0], location[1]]);
            this._map.setPadding(this._padding);
        }
    }

    public changeStyle(style: MapStyle) {
        this._theme = style.theme || Theme.Light;

        // if map is initialized, run change style
        if (this._map) {
            this._mapStateService.loaded = false;
            this._resetMapVariables();
            this._createLayerRenderers();
            this._map.setStyle(style.config.style);
            this._style = style;

            this._loadCustomTileSource();
        }
    }

    public setOpacityOnLayers(layerType: Layers, opacity: number) {
        const layerNames = this.getLayerNames(layerType);

        if (this._map && layerNames) {
            this._mapboxHelper.setOpacityOnLayers(this._map, layerNames, opacity);
        }
    }

    public setCityLabelsVisibility(visible: boolean) {
        if (!this._map) {
            return;
        }

        try {
            const style = this._map.getStyle();
            if (!style || !style.layers) {
                return;
            }

            // Find all symbol layers that are place/settlement labels
            style.layers.forEach(layer => {
                if (layer.type === 'symbol') {
                    const layerId = layer.id.toLowerCase();
                    // Match layers with place, settlement, city, town, village names, poi (districts/neighborhoods), and geographic features
                    if (layerId.includes('place') || 
                        layerId.includes('settlement') || 
                        layerId.includes('city') ||
                        layerId.includes('town') ||
                        layerId.includes('village') ||
                        layerId.includes('hamlet') ||
                        layerId.includes('poi-label') ||
                        layerId.includes('natural-point-label') ||
                        layerId.includes('state-label') ||
                        layerId.includes('country-label') ||
                        layerId.includes('continent-label')) {
                        this._map.setLayoutProperty(
                            layer.id,
                            'visibility',
                            visible ? 'visible' : 'none'
                        );
                    }
                }
            });
        } catch (error) {
            // Style might not be fully ready yet
            return;
        }
    }

    public toggleLineLayer(layerName: string): boolean {
        const lineOpacity = this._map.getPaintProperty(layerName, 'line-opacity');
        this._map.setPaintProperty(layerName, 'line-opacity', lineOpacity === 1 ? 0 : 1);
        return lineOpacity === 1;
    }

    public getLineLayerStatus(layerName: string): boolean {
        if (!this._map) {
            return false;
        }
        try {
            const lineOpacity = this._map.getPaintProperty(layerName, 'line-opacity');
            return lineOpacity === 1;
        } catch (error) {
            return false;
        }
    }

    public setVisibilityOnLayer(layerName: string, visible: boolean) {
        // If the layer is not effected by component lifecycles
        const ignoreLayerHandlingForThese = [Layers.RouteTileset, Layers.StopsTileset, 'traffic'];

        if (this._map) {
            if (_.includes(ignoreLayerHandlingForThese, layerName)) {
                this._mapboxHelper.setVisibilityOnLayers(this._map, [layerName], visible);
            } else {
                this._handleVisibilityOnLayer(layerName, visible);
            }
        }
    }

    public enablePopupOnLayer(layerName: string) {
        const exists = _.includes(this._selectedPopupLayers, layerName);
        if (exists) {
            return;
        }

        this._selectedPopupLayers.push(layerName);
    }

    public removePopupOnLayer(layerName: string) {
        const exists = _.includes(this._selectedPopupLayers, layerName);
        if (exists) {
            this._selectedPopupLayers.splice(this._selectedPopupLayers.indexOf(layerName), 1);
        }
    }

    public layerExists(layerName: string) {
        return this._mapboxHelper.layerExists(this._map, layerName);
    }

    public resetSelectedFeatures(component: string = 'default') {
        this.setSelectedFeatures(undefined, component);
    }

    public setSelectedFeatures(selectedFeatures: {
        featureIds: string[]; idProperty: string | string[];
        layerNames: string[]; color?: string;
    }[], component: string = 'default') {
        this._selectedFeatureCollections[component] = selectedFeatures;

        let features: typeof selectedFeatures;
        if (this._selectedFeatureCollections['stop-point']) {
            features = this._selectedFeatureCollections['stop-point'];
        } else if (this._selectedFeatureCollections['stop-area']) {
            features = this._selectedFeatureCollections['stop-area'];
        } else if (this._selectedFeatureCollections['edit-space']) {
            features = this._selectedFeatureCollections['edit-space'];
        } else {
            features = this._selectedFeatureCollections['default'];
        }

        if (!features) {
            this._clearSelectedFeatures();
            return;
        }

        const layersToFilter = _.flatMap(features, sf => sf.layerNames);

        _.each(layersToFilter, layerToFilter => {
            if (this._map.getLayer(layerToFilter) && !this._defaultFilters[layerToFilter]) {
                this._defaultFilters[layerToFilter] = this._map.getFilter(layerToFilter);
            }
        });

        _.each(features, selectedFeature => {
            _.each(selectedFeature.layerNames, layerName => {
                if (this._map.getLayer(layerName) !== undefined) {
                    const mapFilter: any[] = ['any'];
                    _.each(selectedFeature.featureIds, featureId => {
                        if (_.isArray(selectedFeature.idProperty)) {
                            _.each(selectedFeature.idProperty, idProp => {
                                mapFilter.push(['==', idProp, featureId]);
                            });
                        } else {
                            mapFilter.push(['==', selectedFeature.idProperty, featureId]);
                        }
                    });
                    this._map.setFilter(layerName, mapFilter);
                }
            });
        });
    }

    public setFeatureState(layer: Layers, ids: string[] | string, value: any) {
        const sourceobj = this._mapLayerFactory.getSourceObject(layer);
        const items = _.isArray(ids) ? ids : [ids];
        items.forEach(id => {
            const state = { id, ...sourceobj};
            this._map.setFeatureState(state, value);
        });
    }

    public setLayersToDraw(layers: MapDataSetLayer[]) {
        this._layersToDraw = layers;
    }

    public renderDataSet(layerType: Layers, sourceUrl: string, sourceLayer: string): string {
        return this._mapLayerFactory.renderLayerFromDataSet(
            layerType,
            this._map,
            sourceUrl,
            sourceLayer);
    }

    public renderData(layerType: Layers, features: Feature[], component: string = 'default', force = false) {
        if (!this._mapStateService.loaded && !force) {
            return;
        }

        if (!this._renderedLayerData[layerType]) {
            this._renderedLayerData[layerType] = {};
        }

        if (features?.length === 0) {
            this._renderedLayerData[layerType][component] = undefined;
        } else {
            this._renderedLayerData[layerType][component] = features;
        }

        let featuresToRender: typeof features;
        if (this._renderedLayerData[layerType]['VehicleComponent']) {
            featuresToRender = this._renderedLayerData[layerType]['VehicleComponent'];
        } else if (this._renderedLayerData[layerType]['LineComponent']) {
            featuresToRender = this._renderedLayerData[layerType]['LineComponent'];
        } else if (this._renderedLayerData[layerType]['HistoryComponent']) {
            featuresToRender = this._renderedLayerData[layerType]['HistoryComponent'];
        } else {
            featuresToRender = this._renderedLayerData[layerType]['default'];
        }

        if (featuresToRender === undefined) {
            featuresToRender = [];
        }

        if (layerType === Layers.Vehicle) {
            this._prepareVehicleFeatures(featuresToRender);
        }

        const fc = this._createFeatureCollection(featuresToRender);
        this._mapLayerFactory.renderLayer(layerType, this._map, fc);

        // Delete only this component's record, not the entire layer's data
        if (this._renderedLayerData[layerType][component] === undefined) {
            delete this._renderedLayerData[layerType][component];
        }
    }

    public clearData(layerType: Layers) {
        const fc = this._createFeatureCollection([]);
        this._mapLayerFactory.renderLayer(layerType, this._map, fc);
    }

    public centerMapOnFeatures(features: Feature[]) {
        this._mapboxHelper.fitBoundsOfFeatureCollection(this._map, features, this._padding);
    }

    public getLayerNames(layerType: Layers) {
        return this._mapLayerFactory.getLayersByName(layerType);
    }

    private _clearSelectedFeatures() {
        // Återställ filter om inga features är valda
        for (const key in this._defaultFilters) {
            if (this._defaultFilters.hasOwnProperty(key)) {
                this._map.setFilter(key, this._defaultFilters[key]);
            }
        }
    }

    // recreate the renderers in order to get the right theme
    private _createLayerRenderers() {
        // Register layer renderers (factory pattern)
        // Note: Registration order doesn't control rendering order - that's handled by
        // the initialization sequence in the styledata event handler:
        // 1. _initializeEmptyLayers() creates Vehicle → Stops
        // 2. _renderTilesets() creates StopsTileset
        // 3. _initializeLinesLayer() creates Lines
        // Final render order (bottom to top): Lines → StopsTileset → Stops → Vehicle → City labels
        
        const routeTilesetLayerRenderer = new RenderRouteTilesetLayer(this._mapboxHelper, this._theme);
        routeTilesetLayerRenderer.setSourceLayer(this._routeTilesetName);
        this._mapLayerFactory.addRenderer(Layers.RouteTileset, routeTilesetLayerRenderer);

        this._mapLayerFactory.addRenderer(Layers.DrawRoute, new RenderDrawRouteLayer(this._mapboxHelper, this._theme));

        this._mapLayerFactory.addRenderer(Layers.TimingPoint,
            new RenderTimingPointLayer(this._mapboxHelper, this._theme));

        this._mapLayerFactory.addRenderer(Layers.Vehicle, new RenderVehiclesLayer(this._mapboxHelper, this._theme));

        this._mapLayerFactory.addRenderer(Layers.Stops, new RenderStopsLayer(this._mapboxHelper, this._theme));

        const stopsTilesetLayerRenderer = new RenderStopsTilesetLayer(this._mapboxHelper, this._theme);
        stopsTilesetLayerRenderer.setSourceLayer(this._stopsTilesetName);
        this._mapLayerFactory.addRenderer(Layers.StopsTileset, stopsTilesetLayerRenderer);

        this._mapLayerFactory.addRenderer(Layers.Lines, new RenderLinesLayer(this._mapboxHelper, this._theme, Layers.Lines));
    }

    private _addImages() {

        const vehicleRedAnimated: any = new VehicleIcon(this._map, VEHICLE_COLOR_RGB.Red, true);
        this._addImage('vehicle-red-animated', vehicleRedAnimated);

        const vehicleGreen: any = new VehicleIcon(this._map, VEHICLE_COLOR_RGB.Green);
        this._addImage('vehicle-green', vehicleGreen);

        const vehicleYellow: any = new VehicleIcon(this._map, VEHICLE_COLOR_RGB.Yellow);
        this._addImage('vehicle-yellow', vehicleYellow);

        const vehicleOrange: any = new VehicleIcon(this._map, VEHICLE_COLOR_RGB.Orange);
        this._addImage('vehicle-orange', vehicleOrange);

        const vehiclePink: any = new VehicleIcon(this._map, VEHICLE_COLOR_RGB.Pink, true);
        this._addImage('vehicle-pink', vehiclePink);

        const vehicleGray: any = new VehicleIcon(this._map, VEHICLE_COLOR_RGB.Gray);
        this._addImage('vehicle-gray', vehicleGray);

        const vehicleBlue: any = new VehicleIcon(this._map, VEHICLE_COLOR_RGB.Blue);
        this._addImage('vehicle-blue', vehicleBlue);

        const warningIcon: any = new WarningIcon('rgb(255,109,33)');
        this._addImage('warning-icon', warningIcon);
    }

    private static readonly _vehicleIconConfig: Record<
        string, { color: VEHICLE_COLOR_RGB; animate: boolean }
    > = {
        'vehicle-red-animated': { color: VEHICLE_COLOR_RGB.Red,    animate: true  },
        'vehicle-green':        { color: VEHICLE_COLOR_RGB.Green,  animate: false },
        'vehicle-yellow':       { color: VEHICLE_COLOR_RGB.Yellow, animate: false },
        'vehicle-orange':       { color: VEHICLE_COLOR_RGB.Orange, animate: false },
        'vehicle-pink':         { color: VEHICLE_COLOR_RGB.Pink,   animate: true  },
        'vehicle-gray':         { color: VEHICLE_COLOR_RGB.Gray,   animate: false },
        'vehicle-blue':         { color: VEHICLE_COLOR_RGB.Blue,   animate: false },
    };

    private _prepareVehicleFeatures(features: Feature[]): void {
        for (const f of features) {
            const props = f?.properties as { icon?: string; title?: string; bearing?: number; textBadge?: string } | undefined;
            if (props?.icon) {
                this.ensureVehicleIcon(props.icon);
                props.textBadge = this.ensureVehicleTextBadge(props.icon, props.title);
            }
        }
    }

    private ensureVehicleIcon(iconBase: string): void {
        const cfg = MapboxService._vehicleIconConfig[iconBase];
        if (!cfg || this._map.hasImage(iconBase)) return;
        // _addImages() pre-registers these at startup; this handles re-registration
        // after a style reload clears the Mapbox image registry.
        this._addImage(iconBase, new VehicleIcon(this._map, cfg.color, cfg.animate) as any);
    }

    private ensureVehicleTextBadge(iconBase: string, title: string): string | undefined {
        const cfg = MapboxService._vehicleIconConfig[iconBase];
        if (!cfg || !title) return undefined;
        const name = `badge-${iconBase}-${title}`;
        if (!this._map.hasImage(name)) {
            this._addImage(name, createVehicleTextBadge(title, cfg.color));
        }
        return name;
    }

    private _addImage(name: string, image: HTMLImageElement | ArrayBufferView | {
        width: number;
        height: number;
        data: Uint8Array | Uint8ClampedArray;
    } | ImageData | ImageBitmap) {
        if (!this._map.hasImage(name)) {
            this._map.addImage(name, image, { pixelRatio: 2 });
        }
    }

    // when a map style is reloaded, all the features are gone
    private _resetMapVariables() {
        this._defaultFilters = [];
        this._mapLayerFactory.clear();
    }

    private _handleVisibilityOnLayer(layerName: string, visible: boolean) {
        const alreadySetVisibility = _.includes(this._layerVisibilityNames, layerName);

        if (visible) {
            this._layerVisibilityNames.push(layerName);

            if (alreadySetVisibility) {
                return;
            }
        } else {
            const subjectToRemove = _.find(this._layerVisibilityNames, s => s === layerName);
            if (!subjectToRemove) {
                return;
            }

            // remove the first occurence of the subject in the array
            const firstIndexOfMatch = _.findIndex(this._layerVisibilityNames, s => s === subjectToRemove); // if -1 false
            if (firstIndexOfMatch !== -1) {
                this._layerVisibilityNames.splice(firstIndexOfMatch, 1);
            }

            const foundSubject = _.find(this._layerVisibilityNames, s => s === layerName);
            if (foundSubject) {
                return;
            }
        }

        const allLayers = this._mapLayerFactory.getLayersByName(layerName);
        this._mapboxHelper.setVisibilityOnLayers(this._map, allLayers, visible);
    }

    private _setupMapEventListeners() {
        this._setupMapLoadedListener();
        this._setupMapMoveEndListener();
        this._setupMapRotateListener();
        this._setupMapClickListener();
        this._setupDrawListeners();
        this._setupMapMouseMoveListener();
        this._setupMapDragEndListener();
    }

    private _setupMapDragEndListener() {
        this._map.on('dragend', (event) => {
            this._mapStateService.setDragEvent(false);
        });
        this._map.on('dragstart', (event) => {
            this._mapStateService.setDragEvent(true);
        });
    }

    private _setupDrawListeners() {
        this._map.on('draw.create', (ev) => {
            this._updateDrawFeature(ev);
        });
        this._map.on('draw.delete', (ev) => {
            this._updateDrawFeature(ev);
        });
        this._map.on('draw.update', (ev) => {
            this._updateDrawFeature(ev);
        });
    }

    private _setupDrawRouteListeners() {
        this._drawRouting.drawChange$.pipe(
            filter(value => this._mapStateService.loaded),
        ).subscribe(allFeatures => {
            const fc = this._createFeatureCollection(allFeatures);
            this._mapLayerFactory.renderLayer(Layers.DrawRoute, this._map, fc);
        });

        this._drawRouting.isLoading$.subscribe(loading => {
            this._drawIsLoading = loading;
            this._setCursor();
        });
    }

    private _redrawDrawLayer() {
        const allFeatures = this._drawRouting.drawFeatures;
        if (allFeatures) {
            const fc = this._createFeatureCollection(allFeatures);
            this._mapLayerFactory.renderLayer(Layers.DrawRoute, this._map, fc);
        }
    }

    private _redrawCusomLayerData() {
        const layerTypes = Object.keys(this._renderedLayerData);
        _.each(layerTypes, layerType => {
            const componentKeys = Object.keys(this._renderedLayerData[layerType]);
            _.each(componentKeys, cK => {
                const features = this._renderedLayerData[layerType][cK];
                this.renderData(layerType as Layers, features, cK, true);
            });
        });
    }

    // Handle draw operations on the map
    private _updateDrawFeature(event: any) {
        if (event.features && event.features.length > 0) {
            const f = event.features[0];
            this._mapDrawFeatureChange$.next(f);
        } else {
            this._mapDrawFeatureChange$.next(undefined);
        }
    }

    private _initializeEmptyLayers() {
        // Initialize Vehicle and Stops layers with empty data
        // These must exist before other layers so they can be referenced via layerBefore
        
        // 1. Create Vehicle layer (layerBefore: first symbol layer → renders below city labels)
        const emptyFeatureCollection: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: []
        };
        this._mapLayerFactory.renderLayer(Layers.Vehicle, this._map, emptyFeatureCollection);
        
        // 2. Create Stops layer (layerBefore: Vehicle → renders below Vehicle)
        this.renderData(Layers.Stops, [], 'MapInitialization');
        
        // Next: StopsTileset will be created via _renderTilesets() with layerBefore: Stops
        // Last: Lines will be created via _initializeLinesLayer() with layerBefore: StopsTileset
    }

    private _initializeLinesLayer() {
        // Create Lines layer with layerBefore: StopsTileset → renders below StopsTileset
        // Must be called AFTER StopsTileset exists so the layerBefore reference works
        this.renderData(Layers.Lines, [], 'MapInitialization');
    }

    private _renderTilesets() {
        this.renderDataSet(Layers.StopsTileset, this._stopsTilesetUrl, this._stopsTilesetName);
        this.renderDataSet(Layers.RouteTileset, this._routeTilesetUrl, this._routeTilesetName);

        this._mapboxHelper.setVisibilityOnLayers(
            this._map,
            [Layers.StopsTileset],
            this._appStateService.stateSnapshot.mapConfig?.showStopsNetwork
        );

        this._mapboxHelper.setVisibilityOnLayers(
            this._map,
            [Layers.RouteTileset],
            this._appStateService.stateSnapshot.mapConfig?.showRouteNetwork
        );
    }

    private _setupMapLoadedListener() {

        this._map.on('load', () => {
            this.setVisibilityOnLayer(Layers.Vehicle, true); // always!
        });

        this._map.on('styledataloading', (ev) => {
            this._logger.logEvent('styledataloading', ev);
        });

        this._map.on('styledata', (ev) => {
            if (!this._mapStateService.loaded) {
                this._mapStateService.loaded = true;
                console.log('Map styledata loaded');
                this._logger.logEvent('styledata', ev);

                this._redrawDataSetLayers();
                this._loadCustomTileSource();
                this._redrawDrawLayer();
                this._redrawCusomLayerData();
                this._addImages();
                this._mapboxHelper.setVisibilityOnLayers(this._map, ['traffic'],
                    this._appStateService.isTrafficLayerVisible);

                // Apply city labels visibility from saved config
                this.setCityLabelsVisibility(this._appStateService.isCityLabelsVisible);

                // Layer initialization sequence to establish correct layerBefore positioning:
                // 1. Create tileset sources (sources are lost on style change)
                this._mapboxHelper.createVectorSource(this._map, this._stopsTilesetUrl, Layers.StopsTileset);
                this._mapboxHelper.createVectorSource(this._map, this._routeTilesetUrl, Layers.RouteTileset);
                
                // 2. Initialize Vehicle + Stops (StopsTileset will reference Stops)
                this._initializeEmptyLayers();
                
                // 3. Render tilesets (StopsTileset can now reference Stops with layerBefore)
                this._renderTilesets();
                
                // 4. Initialize Lines (can now reference StopsTileset with layerBefore)
                // Final order: Lines → StopsTileset → Stops → Vehicle → City labels
                this._initializeLinesLayer();
            }
        });

        this._map.on('error', (ev) => {
            this._logger.logException(ev.error);
        });
    }

    private _loadCustomTileSource() {
        if (this._style) {

            if (this._style.config.customTileSource) {
                const sourceId = this._style.config.customTileSource.sourceId;

                if (this._map.getSource(sourceId) === undefined) {

                    this._map.addSource(sourceId, {
                        type: this._style.config.customTileSource.type,
                        tiles: [
                            this._style.config.customTileSource.tiles
                        ],
                        tileSize: this._style.config.customTileSource.tileSize
                    });
                }

                const layerId = this._style.config.customTileSource.layerId;
                if (this._map.getLayer(layerId) !== undefined) {
                    this._map.removeLayer(layerId);
                }

                if (this._map.getLayer(layerId) === undefined) {

                    const layer: mapboxgl.AnyLayer = {
                        id: layerId,
                        type: this._style.config.customTileSource.type,
                        source: sourceId,
                        paint: {}
                    };
                    this._map.addLayer(layer, this._style.config.customTileSource.before);
                }
            }
        }
    }

    private _redrawDataSetLayers() {
        _.each(this._layersToDraw, (dataSetLayer: MapDataSetLayer) => {
            this._mapLayerFactory.renderLayerFromDataSet(
                dataSetLayer.layerType,
                this._map,
                dataSetLayer.sourceUrl,
                dataSetLayer.sourceLayer);
        });
    }

    //#region map event listeners
    private _setupMapMoveEndListener() {
        this._map.on('moveend', () => {
            const mapBearing = this._map.getBearing();

            const center = this._map.getCenter();
            this._mapStateService.setCenter([center.lng, center.lat]);
            this._mapStateService.setZoom(this._map.getZoom());
            this._mapStateService.setPitch(this._map.getPitch());
            this._mapStateService.setBearing(mapBearing);
            this._mapStateService.setPadding(this._map.getPadding());

            const latLng = this._map.getBounds();

            const geo: Geometry = {
                type: 'Polygon',
                coordinates: [[
                    [latLng.getEast(), latLng.getNorth()],
                    [latLng.getWest(), latLng.getNorth()],
                    [latLng.getWest(), latLng.getSouth()],
                    [latLng.getEast(), latLng.getSouth()],
                    [latLng.getEast(), latLng.getNorth()]
                ]]
            };

            this._mapStateService.setBbox(geo, this._map.getBounds().toArray());

            // if last flyTo-location is located outside the new boundingBox.
            if (this._lastFlyTo) {
                const lastDestination: mapboxgl.LngLat = new mapboxgl.LngLat(this._lastFlyTo[0], this._lastFlyTo[1]);
                if (!this._map.getBounds().contains(lastDestination)) {
                    this.flyTo(this._lastFlyTo);
                }
                this._lastFlyTo = undefined;
            }

        });
    }

    //#region Click handlers
    private _setupMapClickListener() {
        this._map.on('click', (event: mapboxgl.MapMouseEvent) => {
            this._normalClickHandler(event);
            this._editRouteClick(event);
            this._selectRouteClick(event);
        });

        this._map.on('mousedown', (e: mapboxgl.MapMouseEvent) => {
            this._lastFlyTo = undefined;
            if (e.originalEvent.button === 0) {
                this._mapStateService.setLeftClick();
            }

            if (e.originalEvent.button === 2) {
                this._mapStateService.setRightClick(e.lngLat,e.point);
                
            }
        });
    }

    private _normalClickHandler(event: mapboxgl.MapMouseEvent) {
        if (this._mapState !== MAP_STATE.VIEW) {
            return;
        }

        const features = this._map.queryRenderedFeatures([event.point.x, event.point.y], {
            layers: this._clickableLayers
        });

        // filtrera på vehicle
        let vehicleInventoryIds = _.chain(features)
            .filter(feature => feature.properties['type'] === 'vehicle')
            .map(feature => ({
                id: feature.properties.inventoryNumber,
                type: 'vehicle',
                hasWarning: feature.properties.hasWarning
            }))
            .filter(feature => feature.id !== undefined)
            .uniqBy('id')
            .value();

        this._mapStateService.setClickedCoordinate(event.lngLat);

        if (vehicleInventoryIds.length && vehicleInventoryIds.some(v => v.hasWarning)) {
            vehicleInventoryIds = vehicleInventoryIds.filter(v => v.hasWarning);
        }
        if (!vehicleInventoryIds.length || vehicleInventoryIds.length > 1) {
            return; // do nothing if several vehicles or none
        }

        this._mapStateService.setItemsClicked(vehicleInventoryIds);
    }

    private async _editRouteClick(event: mapboxgl.MapMouseEvent) {
        if (!this._editRouteDrawState) {
            return;
        }

        // construct the new line with the previous clicked point and the new
        let pos = [event.lngLat.lng, event.lngLat.lat];

        const isOverEndPoint = this._isPointerOverEditRouteRefPoint(event.point, 'end');

        // check if we have started edit
        if (!this._drawRouting.hasStartedDrawLine()) {
            const drawMode = isOverEndPoint ? DRAW_MODE.AFTER : DRAW_MODE.BEFORE;
            this._drawRouting.initBeforeOrAfterEdit(pos, drawMode);
            return;
        }

        // override if the endpoint is clicked, replace it
        if (isOverEndPoint) {
            pos = this._drawRouting.endCoordinate;
        }

        this._drawIsLoading = true;
        this._setCursor();

        const prevPoint = this._drawRouting.getPrevPoint();
        const newLineSegment = await this._getLineStringBetweenPoints(prevPoint, pos);
        this._drawRouting.addNewLineSegment(newLineSegment, pos);

        this._drawIsLoading = false;
        this._setCursor();

        // finish the draw mode for the routed line
        if (isOverEndPoint) {
            this._mapState = MAP_STATE.EDIT_SELECT_ALTERNATE_ROUTE;

            // the new feature
            const newFeature = this._drawRouting.feature;
            this._mapDrawFeatureChange$.next(newFeature);
        }
    }

    private _selectRouteClick(event: mapboxgl.MapMouseEvent) {
        if (!this._editRouteSelectedState) {
            return;
        }

        const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [[event.point.x - 5, event.point.y - 5], [event.point.x + 5, event.point.y + 5]];
        const features = this._map.queryRenderedFeatures(bbox, {
            layers: this._mapLayerFactory.getLayersByName(Layers.DrawRoute)
        });

        // get the lines from the map
        const lineFeatures = _.filter(features, f => f.properties.id !== undefined && f.properties.type === 'line');

        // can only select on at a time
        if (lineFeatures.length > 0) {
            const line = _.first(lineFeatures);
            this._drawRouting.toggleLineSegment(line.properties.id);
        }
    }

    //#endregion

    //#region MouseMove handlers
    private _setupMapMouseMoveListener() {
        this._map.on('mousemove', (event) => {
            this._normalMouseMoveHandler(event);
            this._popupMouseMoveHandler(event);
            this._editRouteMouseMoveHandler(event);
            this._editRouteSelectMouseMoveHandler(event);
        });
    }

    private _editRouteSelectMouseMoveHandler(event: mapboxgl.MapMouseEvent) {
        if (!this._editRouteSelectedState) {
            return;
        }

        // query the possible features
        const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [[event.point.x - 2, event.point.y - 2], [event.point.x + 2, event.point.y + 2]];
        const features = this._map.queryRenderedFeatures(bbox, {
            layers: this._mapLayerFactory.getLayersByName(Layers.DrawRoute)
        });
        const lineFeatures = _.filter(features, f => f.properties.id !== undefined && f.properties.type === 'line');

        // cursor
        this._setCursor(lineFeatures.length > 0 ? 'pointer' : '');

        // popup
        if (lineFeatures.length > 0) {
            const location: any = [event.lngLat.lng, event.lngLat.lat];
            this._displayBasicPopup(location, 'Markera delsträcka');
        } else if (this._popupReference){
            // close popup if exists
            this._mapboxHelper.closePopup(this._popupReference);
        }
    }

    private _editRouteMouseMoveHandler(event: mapboxgl.MapMouseEvent) {
        if (!this._editRouteDrawState) {
            return;
        }

        const isOverEndPoint = this._isPointerOverEditRouteRefPoint(event.point, 'end');
        this._setCursor(isOverEndPoint ? 'pointer' : 'crosshair');

        const location: any = [event.lngLat.lng, event.lngLat.lat];
        if (isOverEndPoint) {
            // beroende på vilket state vi är i, antingen visa sammanfoga linje (om middle) eller om rita i end, visa avsluta
            let title = 'Sammanfoga linje';
            if (this._drawRouting.drawMode === DRAW_MODE.AFTER) {
                title = 'Avsluta';
            }

            if (!this._drawRouting.hasStartedDrawLine() && this._drawRouting.drawMode !== DRAW_MODE.BEFORE) {
                title = 'Förläng linje härifrån';
            }
            this._displayBasicPopup(location, title);
        } else {
            const title = this._drawRouting.hasStartedDrawLine() ? 'Förläng' : 'Skapa ny startpunkt';
            this._displayBasicPopup(location, title);
        }
    }

    private _normalMouseMoveHandler(event: mapboxgl.MapMouseEvent) {
        // do nothing if in default edit mode
        if (this._mapState !== MAP_STATE.VIEW) {
            return;
        }

        const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [[event.point.x - 5, event.point.y - 5], [event.point.x + 5, event.point.y + 5]];
        const features = this._map.queryRenderedFeatures(bbox, {
            layers: this._clickableLayers
        });

        // set pointer if mouseenter on features
        this._setCursor(features.length > 0 ? 'pointer' : '');
    }

    private _popupMouseMoveHandler(event: mapboxgl.MapMouseEvent) {
        // do nothing if in default edit mode
        if (this._mapState !== MAP_STATE.VIEW) {
            this._closeCurrentPopup();
            return;
        }

        // Clear any existing debounce timer
        if (this._popupDebounceTimer) {
            clearTimeout(this._popupDebounceTimer);
            this._popupDebounceTimer = undefined;
        }

        const features = this._map.queryRenderedFeatures([event.point.x, event.point.y], {
            layers: this._safeSelectedPopupLayers
        });

        // Filter features that have an inventoryNumber property
        const filteredFeatures = features.filter(feature => feature.properties?.inventoryNumber);
        if (filteredFeatures.length === 1) {
            const inventoryNumber = filteredFeatures[0].properties.inventoryNumber;
            this._showPopupIfAble(inventoryNumber, event);
            return;
        } else if (filteredFeatures.length) {
            // If multiple features, prefer the one with a warning (if any)
            const warningFeatures = filteredFeatures.filter(feature => feature.properties.hasWarning);
            if (warningFeatures.length === 1) {
                const inventoryNumber = warningFeatures[0].properties.inventoryNumber;
                this._showPopupIfAble(inventoryNumber, event);
                return;
            }
        }
        
        // If no relevant features, close any existing popup
        this._closeCurrentPopup();
    }

    // Only show popup if it's a different vehicle than currently displayed
    private _showPopupIfAble(inventoryNumber: string, event: mapboxgl.MapMouseEvent) {
        if (this._currentPopupInventoryNumber !== inventoryNumber) {
            if (this._popupDebounceTimer) {
                clearTimeout(this._popupDebounceTimer);
                this._popupDebounceTimer = undefined;
            }
            // Debounce the popup display
            this._popupDebounceTimer = window.setTimeout(() => {
                this._currentPopupInventoryNumber = inventoryNumber;
                this._displayPopup(inventoryNumber, event.originalEvent.clientX, event.originalEvent.clientY);
            }, 100); // 100ms debounce
        }
    }

    private _closeCurrentPopup() {
        this._vehicleHoverSubject$.next(null);
        this._currentPopupInventoryNumber = undefined;
        
        if (this._popupDebounceTimer) {
            clearTimeout(this._popupDebounceTimer);
            this._popupDebounceTimer = undefined;
        }
    }
    //#endregion

    private _setupMapRotateListener() {
        this._map.on('rotate', () => {
            const mapBearing = this._map.getBearing();
            this._mapStateService.setBearing(mapBearing);
        });
    }
    //#endregion

    private _setCursor(cursor: string = 'pointer') {
        if (this._drawIsLoading) {
            this._map.getCanvas().style.cursor = 'wait';
            this._prevCursor = 'wait';
        } else if (this._prevCursor !== cursor) {
            this._prevCursor = cursor;
            this._map.getCanvas().style.cursor = cursor;
        }
    }

    private _getLeftOcclusionWidth(): number {
        if (!this._map) {
            return 0;
        }

        const container = this._map.getContainer();
        const mapRect = container.getBoundingClientRect();
        const displayWrapper = container.closest('.display-wrapper') as HTMLElement | null;
        const leftOccluders = (displayWrapper ?? container.parentElement)
            ?.querySelectorAll<HTMLElement>('app-selection-overview, .blade-container');
        if (!leftOccluders || leftOccluders.length === 0) {
            return 0;
        }

        let maxOverlap = 0;
        leftOccluders.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const overlap = Math.max(0, rect.right - mapRect.left);
            maxOverlap = Math.max(maxOverlap, Math.round(overlap));
        });

        // Ignore tiny border-only overlap when blades are closed.
        return maxOverlap <= 5 ? 0 : maxOverlap;
    }

    private _getRightBladeWrapperWidth(): number {
        if (!this._map) {
            return 0;
        }

        const container = this._map.getContainer();
        const displayWrapper = container.closest('.display-wrapper') as HTMLElement | null;
        const rightWrapper = (displayWrapper ?? container.parentElement)
            ?.querySelector<HTMLElement>('.right-blade-wrapper');
        if (!rightWrapper) {
            return 0;
        }

        // Closed right blades can still contribute a few border pixels.
        const width = Math.max(0, Math.round(rightWrapper.getBoundingClientRect().width));
        return width <= 5 ? 0 : width;
    }

    //#region Methods handeling the own draw route
    private _isPointerOverEditRouteRefPoint(point: mapboxgl.Point, pointType: string) {
        const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [[point.x - 2, point.y - 2], [point.x + 2, point.y + 2]];
        const features = this._map.queryRenderedFeatures(bbox, {
            layers: this._mapLayerFactory.getLayersByName(Layers.DrawRoute)
        });
        // filter out the points
        let points =
            _.map(features, (feature: any) => ({
                type: feature.properties.type,
                pointType: feature.properties.pointType,
            }));
        points = _.filter(points, item => item.type === 'refpoint' && item.pointType === pointType);
        return points.length > 0;
    }

    private async _getLineStringBetweenPoints(start: any, to: any): Promise<Feature<LineString>> {
        if (this._routingProfile === ROUTING_PROFILE.FREE_MODE) {
            const newline = turf.lineString([start, to]);
            return newline;
        }

        const newRoute = await this._routingService.getDirectionsRoute(this._routingProfile, start, to);
        return turf.feature(newRoute);
    }

    private get _editRouteSelectedState() {
        return this._mapState === MAP_STATE.EDIT_SELECT_ALTERNATE_ROUTE;
    }

    private get _editRouteDrawState() {
        return this._mapState === MAP_STATE.EDIT_DRAW_ALTERNATE_ROUTE;
    }

    //#endregion

    private get _clickableLayers() {
        let clickableLayers = this._mapLayerFactory.allRenderedLayerNames;
        const defaultLayers = this._getDefaultClickableLayersIfExists();
        const allLayers = clickableLayers.concat(defaultLayers);
        
        // Filter out layers that don't actually exist in the map to prevent query errors
        return allLayers.filter(layerName => {
            try {
                return this._map?.getLayer(layerName);
            } catch {
                return false;
            }
        });
    }

    private get _safeSelectedPopupLayers() {
;
        // Filter out layers that don't actually exist in the map to prevent query errors
        return this._selectedPopupLayers.filter(layerName => {
            try {
                return this._map?.getLayer(layerName);
            } catch {
                return false;
            }
        });
    }

    // layers that are added in the mapbox studio that needs to be referenced
    private _getDefaultClickableLayersIfExists() {
        const layerNames = ['gp-traffic-accidents'];
        const output = [];
        _.each(layerNames, layer => {
            if (this._map.getLayer(layer)) {
                output.push(layer);
            }
        });
        return output;
    }

    private _displayBasicPopup(location: any, title: string) {
        // close popup if exists
        if (this._popupReference) {
            this._mapboxHelper.closePopup(this._popupReference);
        }

        const html = this._htmlPopupHelper.createBasicPoup({
            title
        });
        this._mapboxHelper.addPopupWithText(this._map, location, this._popupReference, html);
    }

    private _displayPopup(inventoryNumber: string, clientX: number, clientY: number) {
        this._vehicleStore.getVehicleState(inventoryNumber).pipe(
            take(1)
        ).subscribe((vehicle) => {
            if (vehicle) {
                this._vehicleHoverSubject$.next({ vehicle, clientX, clientY });
            }
        });
    }

    private _createFeatureCollection(features: any[] | any): any {
        if (!(features instanceof Array)) {
            features = [features];
        }
        return turf.featureCollection(features);
    }
}